<script setup lang="ts">
import { reactive, ref, watchEffect } from 'vue';

import {
  TSCalendar,
  TSFilterContainer,
  TSMultiSelect,
  useToast,
} from 'tsv2-library';

import BorrowingServices from '@/services/borrowing.service';

import { BorrowingRequestFilters } from '@/types/filter.type';
import {
  BorrowingRequestFilterOptions,
  BorrowingRequestLoadingFilters,
} from '@/types/filterOptions.type';
import { BorrowingRequestFilterParams } from '@/types/params.type';
import { parseFilter } from '@/utils/parseFilter.util';

const emit = defineEmits<{
  applyFilter: [data: BorrowingRequestFilterParams];
}>();

const props = defineProps<{
  transactionId: string;
  type?: 'History';
  isBorrowed?: boolean;
}>();

const toast = useToast();

const filter = reactive<BorrowingRequestFilters>({});
const loadingFilter = ref<BorrowingRequestLoadingFilters>({});
const filterOptions = ref<BorrowingRequestFilterOptions>({});

const getFilterOptions = async (
  optionField: keyof BorrowingRequestLoadingFilters,
): Promise<void> => {
  try {
    loadingFilter.value[optionField] = true;

    const { data } =
      await BorrowingServices.getBorrowingTransactionRequestOptions(
        props.transactionId,
        {
          [optionField]: true,
        },
      );

    filterOptions.value = {
      ...filterOptions.value,
      [optionField]: data.data[optionField],
    };
  } catch (error) {
    console.error(error);
    toast.add({
      error,
      message: `Error, failed to fetch ${optionField} filter options`,
    });
  } finally {
    loadingFilter.value[optionField] = false;
  }
};

/**
 * This function clear the selected filters by looping the keys of selected filter and delete it.
 */
const clearFilter = (): void => {
  const keys = Object.keys(filter) as Array<keyof BorrowingRequestFilterParams>;

  keys.forEach((key) => {
    delete filter[key];
  });
};

/**
 * Watch for changed filters
 * - if the filter have no selected data, delete the field.
 */
watchEffect(() => {
  const fields = Object.keys(filter) as Array<
    keyof BorrowingRequestFilterParams
  >;

  fields.forEach((field) => {
    if (!filter[field]?.length) delete filter[field];
  });
});
</script>
<template>
  <TSFilterContainer
    @apply="emit('applyFilter', parseFilter(filter))"
    @clear="clearFilter"
    data-ts-name="borrow-detail-filter"
  >
    <TSMultiSelect
      v-model="filter.name"
      :loading="loadingFilter.nameOptions"
      :options="filterOptions.nameOptions"
      @show="getFilterOptions('nameOptions')"
      data-ts-section="asset-name-filter-input"
      label="Name"
      option-label="label"
      option-value="value"
      placeholder="Select asset name"
    />
    <TSMultiSelect
      v-model="filter.brand"
      :loading="loadingFilter.brandOptions"
      :options="filterOptions.brandOptions"
      @show="getFilterOptions('brandOptions')"
      data-ts-section="brand-filter-input"
      label="Brand"
      option-label="label"
      option-value="value"
      placeholder="Select brand"
    />
    <TSMultiSelect
      v-model="filter.model"
      :loading="loadingFilter.modelOptions"
      :options="filterOptions.modelOptions"
      @show="getFilterOptions('modelOptions')"
      data-ts-section="model-filter-input"
      label="Model/Type"
      option-label="label"
      option-value="value"
      placeholder="Select model/type"
    />
    <TSMultiSelect
      v-if="type !== 'History'"
      v-model="filter.status"
      :options="filterOptions.statusOptions"
      @show="getFilterOptions('statusOptions')"
      data-ts-section="status-filter-input"
      label="Status"
      option-label="label"
      option-value="label"
      placeholder="Select status"
    />
    <TSCalendar
      v-if="isBorrowed"
      v-model="filter.dueDate"
      data-ts-section="due-return-date-filter-input"
      label="Due Return Date"
      mode="range"
    />
    <TSCalendar
      v-if="isBorrowed"
      v-model="filter.returnDate"
      data-ts-section="actual-return-date-filter-input"
      label="Actual Return Date"
      mode="range"
    />
  </TSFilterContainer>
</template>
